/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import isNil from 'lodash.isnil';
import {useRefCallback} from '@ion/applications/src/hooks/useRefCallback';
import DashboardFrameManager from '@ion/charting/src/bis/dashboard/DashboardFrameManager';
import useElementResize from '@ion/core/src/hooks/useElementResize';
import {translate} from '@ion/core/src/locale/locale';
import {DashboardId} from '@ion/core/src/services/Sisense';
import {GenericObject} from '@ion/core/src/types/entities';
import IonSpinner from '@ion/dls/src/components/loading/IonSpinner';
import ViewSubGroupsSwitch from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import {ScheduledReportsDeletePrompt} from '@ion/dls/src/editors/ScheduledReportsDeletePrompt';
import IonGridContainer from '@ion/dls/src/grid/IonGridContainer';
import useMedia from '@ion/dls/src/hooks/useMedia';
import {useViewSubGroups} from '@ion/dls/src/hooks/useViewSubGroups';
import Render from '@ion/dls/src/render/Render';
import {dlsPageHeaderTitleLineHeight, toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsLayout} from '@ion/dls/src/theme/theme';
import {ViewSubGroupsPosition} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import withSignals from '../../components/hoc/withSignals';
import SisenseSDKFrame from '../../sisense/SisenseSDKFrame';
import HomeView from './HomeView';
import MigrationResultsGrid from './MigrationResultsGrid';
import ScheduledReportEditorController from './ScheduledReportEditorController';
import ScheduledReportsGrid from './ScheduledReportsGrid';
import {
  deletingScheduledReport,
  headerTab,
  setSelectedDashboard
} from './store';
import {Headertabs, SideNavTabs} from './types';

const ANALYTICS_FRAME_ID                = 'analytics-dashboard';
export const ANALYTICS_FRAME_CONTENT_ID = `${ANALYTICS_FRAME_ID}-content`;
const QUERY_PARAMS                      = '&l=false';

const SETTINGS = {
  showLeftPane  : false,
  showToolbar   : true,
  showRightPane : true
};

/**
 * Styles for the DashboardContent component.
 */
const useStyles = makeStyles((theme) => ({
  loader : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    width          : '100%',
    height         : '100%'
  },
  grid : toggleThemeStyle({
    width                          : 'calc(100% - 48px)',
    padding                        : 24,
    height                         : 'calc(100vh - 332px)',
    [theme.breakpoints.down('sm')] : {
      height  : 'calc(100vh - 392px)',
      padding : 4,
      width   : 'calc(100% - 32px)'
    }
  }, {
    height                         : `calc(100vh - ${dlsLayout('header', 'desktop')}px)`,
    display                        : 'flex',
    minHeight                      : 'auto',
    overflow                       : 'hidden',
    flexDirection                  : 'column',
    [theme.breakpoints.down('sm')] : {
      height : `calc(100vh - ${dlsLayout('header', 'mobile')}px)`,
      margin : '0px'
    }
  }),
  pageHeader : toggleThemeStyle({
    display                        : 'flex',
    marginBottom                   : '20px',
    [theme.breakpoints.down('sm')] : {
      margin : '0px'
    }
  }, {
    width         : '158px',
    height        : '123px',
    display       : 'flex',
    padding       : '28px 16px',
    alignItems    : 'flex-start',
    flexDirection : 'column'
  }),
  titleHeader : {
    marginTop : toggleThemeStyle('5px', '0')
  },
  subAccount : toggleThemeStyle({
    paddingLeft : '35px',
    marginTop   : '12px'
  }, {
    width         : '100%',
    marginTop     : '0px',
    marginRight   : '0px',
    paddingLeft   : '0px',
    paddingTop    : '8px',
    paddingBottom : '8px'
  }),
  titleLabel : {
    color         : toggleThemeStyle('inherit', dlsColor('grey2')),
    width         : 'max-content',
    margin        : 0,
    fontSize      : toggleThemeStyle('inherit', '32px !important'),
    fontStyle     : 'normal',
    fontFamily    : toggleThemeStyle('inherit', 'Roboto !important'),
    fontWeight    : toggleThemeStyle('inherit', '400'),
    lineHeight    : dlsPageHeaderTitleLineHeight,
    letterSpacing : toggleThemeStyle('inherit', '1px !important')
  }
}) as any);

/**
 * Props for the DashboardContent component.
 */
export interface DashboardContentProps {
  selectedDashboard?: {
    oid: string;
    [key: string]: any;
  };
  categories: GenericObject[];
  allCategories: GenericObject[];
  toggleCategory: (category: string, value?: boolean, tab?: SideNavTabs) => void;
  selectedCategory: string | null;
  setSelected: (dashboard: any) => void;
  onSavedReportLoad?: (dashboard) => void;
  onShowSnackbar?: (
    message?: ReactNode,
    type?: string,
    options?: {showScheduledReportLink?: boolean}
  ) => void;
  sortByDate?: boolean;
  isLoading?: boolean;
  route?: string;
  user?: any;
  setSideTab: Function;
  active?: boolean;
  isSchedulerDirector?: boolean;
  isSchedulerFleetManager?: boolean;
  setCategoryFilter: (categories: string[], headerTab?: Headertabs, sideTab?: SideNavTabs) => void;
  categoryFilter: string[];
  omitReportFilter?: boolean;
};

/**
 * DashboardContent is a dynamic content renderer that conditionally displays
 * either a Sisense dashboard (via an iframe) or a HomeView landing screen
 * based on the application's current state.This component is typically used
 * as the central visual panel of an analytics or BI dashboard application.
 * When a specific dashboard is selected (i.e., `selectedDashboard` is provided),
 * it renders an embedded Sisense dashboard using the `SisenseFrame` component.
 * The iframe is constructed using the `sisenseBaseURL` from the environment
 * and the provided `path`.If no dashboard is selected, the component falls back
 * to rendering a landing or discovery screen using the `HomeView` component.
 * This view helps users explore available dashboards by category,
 * apply sorting, and select a dashboard to view. While data is loading or
 * initialization is taking place, a loading spinner (`IonSpinner`) is displayed
 * in a fully centered layout.
 *
 * ```typescript
 * <DashboardContent selectedDashboard={{oid: 'dashboard_123'}}
 *   categories={['Sales', 'Finance', 'Marketing']}
 *   toggleCategory={(category) => setActiveCategory(category)}
 *   selectedCategory="Sales"
 *   setSelected={(dashboard) => setCurrentDashboard(dashboard)}
 *   isLoading={false}
 *   sortByDate />
 * ```
 */
const DashboardContent = ({
  selectedDashboard,
  categories,
  allCategories,
  categoryFilter,
  toggleCategory,
  onSavedReportLoad,
  setCategoryFilter,
  selectedCategory,
  onShowSnackbar,
  setSelected,
  route,
  user,
  setSideTab,
  active = true,
  omitReportFilter = true,
  sortByDate = false,
  isLoading = false,
  isSchedulerDirector = false,
  isSchedulerFleetManager = false
}: DashboardContentProps) => {
  const classes                              = useStyles();
  const scheduledGridRef                     = useRef({});
  const migrationGridRef                     = useRef({});
  const {isDesktop}                          = useMedia();
  const onScheduledRefresh                   = useRefCallback(scheduledGridRef, 'onRefresh');
  const refreshCurrentBlock                  = useRefCallback(scheduledGridRef, 'refreshCurrentBlock');
  const onScheduledReportSaved               = useRefCallback(scheduledGridRef, 'updateRowNode');
  const onMigrationRefresh                   = useRefCallback(migrationGridRef, 'onRefresh');
  const dimensions                           = useElementResize(ANALYTICS_FRAME_CONTENT_ID);
  const selectedDashboardRef                 = useRef(selectedDashboard);
  const [isSDKLoaded, setIsSDKLoaded]        = useState(false);
  const [viewSubGroups, toggleViewSubGroups] = useViewSubGroups();

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const checkSDKLoaded = () => {
      const frameRef   = DashboardFrameManager.getFrameInstance(ANALYTICS_FRAME_ID);
      const existingId = frameRef?.dashboard?.iframeClient?._cid;
      const loaded     = !!existingId;

      const t = setTimeout(() => {
        setIsSDKLoaded(loaded);
        clearTimeout(t);
      }, loaded ? 500 : 0);

      if (loaded && intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    if (isLoading) {
      if (intervalId) clearInterval(intervalId);
      setIsSDKLoaded(false);
    } else if (!isSDKLoaded && selectedDashboard?.oid) {
      checkSDKLoaded();
      intervalId = setInterval(checkSDKLoaded, 350);
    }
  }, [isLoading, isSDKLoaded, selectedDashboard?.oid]);

  useEffect(() => {
    selectedDashboardRef.current = selectedDashboard;
  }, [selectedDashboard]);
  
  const onHomeView = useCallback(() => {
    const category = selectedDashboardRef.current?.categoryName;
    const sideTab  = selectedDashboardRef.current?.tab;
    !isNil(sideTab) && setSideTab(sideTab);
    category && toggleCategory(category, true, sideTab);
    setSelectedDashboard(null);
  }, []);

  const shouldShowScheduledReports = (
    headerTab.value === Headertabs.ScheduledReports &&
    !selectedDashboard?.oid
  );

  const shouldShowMigrationResults = (
    headerTab.value === Headertabs.MigrationResults &&
    !selectedDashboard?.oid
  );

  if (shouldShowScheduledReports) {
    return (
      <IonGridContainer>
        <div className={classes.grid}>
          <Render.If condition={!isLoading}>
            <>
              <Render.If condition={isDesktop}>
                <div className={classes.pageHeader}>
                  <div className={classes.titleHeader}>
                    <HeaderTypography variant='h1' className={classes.titleLabel}>
                      {translate('sisense:analytics.gridTitle')}
                    </HeaderTypography>
                  </div>
                  <div className={classes.subAccount}>
                    <ViewSubGroupsSwitch checked={viewSubGroups}
                      isCtcAdminDashboard
                      onChecked={toggleViewSubGroups}
                      position={ViewSubGroupsPosition.GRID_CTAS}
                      labelColor={dlsColor('grey2')} />
                  </div>
                </div>
              </Render.If>
              <Render.If condition={active}>
                <ScheduledReportsGrid ref={scheduledGridRef}
                  viewSubGroups={viewSubGroups}
                  isSchedulerDirector={isSchedulerDirector}
                  isSchedulerFleetManager={isSchedulerFleetManager}
                  toggleViewSubGroups={toggleViewSubGroups} />
              </Render.If>
              <ScheduledReportsDeletePrompt onRefresh={refreshCurrentBlock}
                deletingItem={deletingScheduledReport.value} />
              <ScheduledReportEditorController onRefresh={refreshCurrentBlock}
                onUpdateRow={onScheduledReportSaved}
                onShowSnackbar={onShowSnackbar} />
            </>
          </Render.If>
        </div>
      </IonGridContainer>
    );
  } else if (shouldShowMigrationResults) {
    return (
      <div className={classes.grid}>
        <Render.If condition={!isLoading}>
          <>
            <MigrationResultsGrid ref={migrationGridRef} user={user} />
            <ScheduledReportEditorController onRefresh={refreshCurrentBlock}
              onUpdateRow={onScheduledReportSaved}
              onShowSnackbar={onShowSnackbar} />
          </>
        </Render.If>
      </div>
    );
  } else if (!isLoading || selectedDashboard?.oid) {
    return (
      <>
        <Render.If condition={!selectedDashboard}>
          <HomeView categories={categories}
            allCategories={allCategories}
            selectedCategory={selectedCategory}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            toggleCategory={toggleCategory}
            omitReportFilter={omitReportFilter}
            setSelected={setSelected}
            setSideTab={setSideTab}
            sortByDate={sortByDate} />
        </Render.If>
        <Render.If condition={!isLoading}>
          <SisenseSDKFrame dashboardId={
            (isSDKLoaded ? selectedDashboard?.oid : DashboardId.InitialDashboard) as any
          }
          overrideStyle={(!isSDKLoaded && !selectedDashboard?.oid) ? {display: 'none'} : {}}
          showFrame={!!selectedDashboard?.oid && active && isSDKLoaded}
          isLoading={!isSDKLoaded}
          onSavedReportLoad={onSavedReportLoad}
          iFrameId={ANALYTICS_FRAME_ID}
          queryParams={QUERY_PARAMS}
          onHomeView={onHomeView}
          isNavCollapsed={false}
          styles={dimensions}
          settings={SETTINGS}
          route={route}
          persistIFrame
          isEditMode />
        </Render.If>
        <Render.If condition={!isLoading}>
          <ScheduledReportEditorController onRefresh={refreshCurrentBlock}
            onUpdateRow={onScheduledReportSaved}
            onShowSnackbar={onShowSnackbar} />
        </Render.If>
      </>
    );
  }
  return (
    <div className={classes.loader}>
      <IonSpinner />
    </div>
  );
};

export default withSignals(DashboardContent);
